/**
 * API Gate - Centralized mechanism to hold/release REST API calls.
 *
 * On mobile app startup, the gate starts HELD (via holdAPIs() called in main.tsx)
 * to prevent any API calls from firing before the OTA compatibility check completes.
 *
 * The OTA provider calls releaseAPIs() once it determines APIs are safe to proceed:
 * - No update needed
 * - Update is backwards compatible
 * - Native update required (handled separately by native modal)
 *
 * If the update is NOT backwards compatible, the gate stays held until the app reloads
 * after the forced OTA update, which naturally resets the module state.
 *
 * The gate is purely explicit: it stays held until releaseAPIs() is called.
 * If something goes wrong, the build can be rejected from the backend.
 */

let gatePromise: Promise<void> | null = null;
let gateResolver: (() => void) | null = null;

/**
 * Hold all API calls by creating a pending promise that waitForAPIGate() will await.
 * The gate stays held until releaseAPIs() is called explicitly.
 */
export const holdAPIs = () => {
  // If already held, don't create a new promise
  if (gatePromise) return;

  gatePromise = new Promise<void>((resolve) => {
    gateResolver = resolve;
  });
};

/**
 * Release all held API calls by resolving the gate promise.
 */
export const releaseAPIs = () => {
  if (gateResolver) {
    gateResolver();
    gateResolver = null;
  }

  gatePromise = null;
};

/**
 * Await this before every API call. Returns immediately if no gate is held,
 * otherwise waits until releaseAPIs() is called.
 */
export const waitForAPIGate = async (): Promise<void> => {
  if (gatePromise) {
    await gatePromise;
  }
};
